import React, { useRef, useEffect, useState } from 'react'
import { X, Save, Upload, Trash, File, Play, Pause, LineChart } from 'lucide-react'
import { formatDuration, getRealDurationMinutes } from '../../utils'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { useMeasurement } from '../../contexts/measurementContext'
import { saveGameDataLocal, loadGameDataLocal, deleteGameData, exportGameData, formatUnderscoreCase } from '../../utils'
import { GamePlayData } from '@/types'
import { stopAllHigherAutomations, setAutomation } from '@/engine/automations'
import { Link } from 'react-router-dom'

interface DebugToolsProps {
  isDebugOpen: boolean
  setIsDebugOpen: (isOpen: boolean) => void
}

export const DebugTools: React.FC<DebugToolsProps> = ({ isDebugOpen, setIsDebugOpen }) => {
  const { gameData, setGameData, gameSpeed, setGameSpeed, isAutoplayEnabled, setIsAutoplayEnabled } =
    useGameplayContext()
  const { startRecording, stopRecording, clearSnapshots } = useMeasurement()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const speedMultipliers = [1, 2, 3, 5, 10, 20, 50, 100, 500, 1000]

  // Handle autoplay quest updates
  useEffect(() => {
    if (isAutoplayEnabled && gameData.activeMainQuest) {
      const checkInterval = setInterval(() => {
        if (!gameData.activeMainQuest && isAutoplayEnabled) {
          handleAutoplay()
        }
        if (gameData.automations.quest_complete.key !== gameData.activeMainQuest) {
          // If current automation quest is different from active quest, update it
          setGameData({
            ...gameData,
            automations: {
              ...gameData.automations,
              quest_complete: {
                ...gameData.automations.quest_complete,
                isActive: true,
                key: gameData.activeMainQuest as any
              }
            }
          })
        }
      }, 1000) // Check every second

      return () => clearInterval(checkInterval)
    }
  }, [isAutoplayEnabled, gameData.activeMainQuest, gameData.automations.quest_complete.key])

  const handleAutoplay = () => {
    const newAutoplayState = !isAutoplayEnabled
    setIsAutoplayEnabled(newAutoplayState)

    if (newAutoplayState && gameData.activeMainQuest) {
      // Enable quest automation and set max speed
      setGameSpeed(100)
      startRecording() // Start recording measurements
      setGameData({
        ...gameData,
        automations: {
          ...gameData.automations,
          quest_complete: {
            ...gameData.automations.quest_complete,
            isActive: true,
            key: gameData.activeMainQuest as any
          }
        }
      })
    } else {
      // Disable quest automation, reset speed, and stop all higher automations
      setGameSpeed(1)
      stopRecording() // Stop recording measurements
      let newData = setAutomation(gameData, 'work', false)
      newData = setAutomation(newData, 'surf', false)
      setGameData(stopAllHigherAutomations(newData))
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target?.result as string)
          saveGameDataLocal(importedData)
          setGameData(importedData)
        } catch (error) {
          console.error('Error importing game data:', error)
          alert('Error importing game data. Please check the file format.')
        }
      }
      reader.readAsText(file)
    }
    // Reset the input so the same file can be imported again if needed
    event.target.value = ''
  }

  const handleExport = () => {
    const { url, filename } = exportGameData()
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Add useEffect to handle the interval cleanup
  useEffect(() => {
    return () => {
      // This will clean up any lingering intervals when the component unmounts
      const intervals = window.setInterval(() => {}, 0)
      for (let i = 0; i < intervals; i++) {
        window.clearInterval(i)
      }
    }
  }, [])

  return (
    <div
      className={`fixed inset-y-0 right-0 w-64 bg-gray-900 bg-opacity-90 backdrop-blur-sm text-white p-4 transform transition-transform duration-300 ease-in-out z-[60] ${
        isDebugOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <button className="absolute top-2 left-2 text-white" onClick={() => setIsDebugOpen(false)}>
        <X className="w-6 h-6" />
      </button>
      <br />
      <h2 className="text-xl font-bold mb-4">Debug Tools</h2>
      <div className="space-y-4">
        <div className="flex space-x-2">
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors"
            onClick={() => saveGameDataLocal(gameData)}
          >
            <Save className="w-4 h-4 inline-block mr-2" />
            Save
          </button>
          <button
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded transition-colors"
            onClick={() => setGameData(loadGameDataLocal())}
          >
            <Upload className="w-4 h-4 inline-block mr-2" />
            Load
          </button>
          <button
            className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded transition-colors"
            onClick={() => deleteGameData()}
          >
            <Trash className="w-4 h-4 inline-block mr-2" />
            Delete
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded transition-colors"
            onClick={handleExport}
          >
            <File className="w-4 h-4 inline-block mr-2" />
            Export
          </button>
          <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json" className="hidden" />
          <button
            className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded transition-colors"
            onClick={handleImportClick}
          >
            <Upload className="w-4 h-4 inline-block mr-2" />
            Import
          </button>
        </div>

        <div>
          <label htmlFor="gameSpeed" className="block mb-2">
            Game Speed: {gameSpeed}x
          </label>
          <select
            id="gameSpeed"
            value={gameSpeed}
            onChange={(e) => setGameSpeed(Number(e.target.value))}
            className="w-full bg-gray-700 text-white rounded px-2 py-1"
          >
            {speedMultipliers.map((speed) => (
              <option key={speed} value={speed}>
                {speed}x
              </option>
            ))}
          </select>
        </div>
        <div>
          <h3 className="font-bold mb-2">Time:</h3>
          <div className="space-y-1">
            <p>Game: {formatDuration(gameData.gameMinutes)}</p>
            <p>Real: {formatDuration(getRealDurationMinutes(gameData.tick))}</p>
            <p className="text-sm text-gray-400">(Tick: {gameData.tick})</p>
          </div>
        </div>
        <button
          className={`w-full ${
            gameData.automations.quest_complete.isActive
              ? 'bg-yellow-500 hover:bg-yellow-600'
              : 'bg-green-500 hover:bg-green-600'
          } text-white font-bold py-2 px-4 rounded transition-colors ${
            !gameData.activeMainQuest ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={handleAutoplay}
          disabled={!gameData.activeMainQuest}
        >
          {gameData.automations.quest_complete.isActive ? (
            <>
              <Pause className="w-4 h-4 inline-block mr-2" />
              Stop Autoplay
            </>
          ) : (
            <>
              <Play className="w-4 h-4 inline-block mr-2" />
              Start Autoplay
            </>
          )}
        </button>
        <Link
          to="/measure"
          className="block w-full mt-2 bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-2 px-4 rounded transition-colors text-center"
        >
          <LineChart className="w-4 h-4 inline-block mr-2" />
          View Measurements
        </Link>
        {/* Active Automations */}
        <div className="text-sm">
          <h3 className="font-bold mb-1">Active Automations:</h3>
          <div className="space-y-0.5">
            {Object.entries(gameData.automations)
              .filter(([key, auto]) => auto.isActive && !['rest', 'work', 'surf'].includes(key))
              .map(([key, auto]) => (
                <div key={key} className="text-gray-300">
                  <b>{formatUnderscoreCase(key)}</b>: <span className="text-blue-300">{auto.key || '-'}</span>
                  <span className="text-green-300">{auto.value || ''}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}
